import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Search, X, Upload, Eye, Clock, User, Menu, Package, ShoppingCart, Save, Settings, ChevronsLeft, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { BASE_URL } from '../Api/Config';
import io from 'socket.io-client';
// import { toast } from 'react-toastify';
import axios from 'axios'

// Sidebar Component
const Sidebar = ({ activeSection, setActiveSection, isOpen, setIsOpen }) => {
  const navigate = useNavigate();
  const menuItems = [

    { id: 'foods', label: 'Food Management', icon: Package },
    { id: 'orders', label: 'Orders', icon: ShoppingCart },
    { id: 'Transaction', label: 'Transaction Details', icon: Clock },
    { id: 'settings', label: 'Restaurant Settings', icon: Settings },
    { id: 'home', label: 'GrabGrub Page', icon: ChevronsLeft, path: '/' },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-screen w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:z-auto
      `}>
        {/* Header */}
        <div className="p-4 lg:p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg lg:text-xl font-bold text-gray-800">Restaurant Admin</h2>
            <button
              onClick={() => setIsOpen(false)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-2 lg:p-4">
          <ul className="space-y-1 lg:space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => {
                      if (item.path) {
                        navigate(item.path); // Navigate to homepage if path exists
                      } else {
                        setActiveSection(item.id); // Else handle as internal section
                      }
                      setIsOpen(false); // Close sidebar on mobile
                    }}
                    className={`w-full flex items-center px-3 lg:px-4 py-2 lg:py-3 text-left rounded-lg transition-colors text-sm lg:text-base
                      ${activeSection === item.id
                        ? 'bg-blue-100 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'}
                    `}
                  >
                    <Icon className="w-4 lg:w-5 h-4 lg:h-5 mr-2 lg:mr-3" />
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </>
  );
};

const FoodManagement = () => {
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingFood, setEditingFood] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const filteredFoods = foods.filter(food =>
    food.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    food.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredFoods.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentFoods = filteredFoods.slice(startIndex, endIndex);


  const [formData, setFormData] = useState({
    name: '',
    category: '',
    price: '',
    description: '',
    image: null,
    availability: true
  });
  // Get auth token from localStorage
  const getAuthToken = () => {
    return localStorage.getItem('token');
  };

  // Fetch foods from API
  const fetchFoods = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await fetch(`${BASE_URL}/restaurant/foods`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFoods(data.foods || []);
      } else {
        console.error('Failed to fetch foods');
      }
    } catch (error) {
      console.error('Error fetching foods:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load foods on component mount
  useEffect(() => {

    fetchFoods();
    // eslint-disable-next-line
  }, []);




  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    try {
      const token = getAuthToken();
      const formDataToSend = new FormData();

      formDataToSend.append('name', formData.name);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('price', parseFloat(formData.price));
      formDataToSend.append('description', formData.description);

      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      let response;
      if (editingFood) {
        // Update existing food
        response = await fetch(`${BASE_URL}/restaurant/food/${editingFood._id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataToSend
        });
      } else {
        // Add new food
        response = await fetch(`${BASE_URL}/restaurant/food`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataToSend
        });
      }

      if (response.ok) {
        const data = await response.json();
        if (editingFood) {
          setFoods(prev => prev.map(food =>
            food._id === editingFood._id ? data.food : food
          ));
        } else {
          setFoods(prev => [...prev, data.food]);
        }
        resetForm();
      } else {
        console.error('Failed to save food');
        alert('Failed to save food item');
      }
    } catch (error) {
      console.error('Error saving food:', error);
      alert('Error saving food item');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      price: '',
      description: '',
      image: null,
      availability: true
    });
    setImagePreview('');
    setEditingFood(null);
    setShowModal(false);
  };

  const handleEdit = (food) => {
    setEditingFood(food);
    setFormData({
      name: food.name,
      category: food.category,
      price: food.price.toString(),
      description: food.description,
      image: null,
      availability: food.availability !== false
    });
    setImagePreview(food.image);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this food item?')) {
      try {
        const token = getAuthToken();
        const response = await fetch(`${BASE_URL}/restaurant/food/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          setFoods(prev => prev.filter(food => food._id !== id));
        } else {
          console.error('Failed to delete food');
          alert('Failed to delete food item');
        }
      } catch (error) {
        console.error('Error deleting food:', error);
        alert('Error deleting food item');
      }
    }
  };

  const openImageModal = (image) => {
    setSelectedImage(image);
    setShowImageModal(true);
  };

  const handleToggleAvailability = async (foodId) => {
    try {
      const token = localStorage.getItem('token');

      // Find the current food item
      const foodItem = foods.find(food => food._id === foodId);
      if (!foodItem) {
        // toast.error('Food item not found.');
        return;
      }

      const newAvailability = !foodItem.isAvailable;

      const response = await axios.patch(
        `${BASE_URL}/restaurant/foods/${foodId}/toggle-availability`,
        { isAvailable: newAvailability },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        // toast.success(response.data.message);

        // Update foods list
        setFoods(prevFoods =>
          prevFoods.map(food =>
            food._id === foodId ? { ...food, isAvailable: newAvailability } : food
          )
        );
      } else {
        // toast.error(response.data.message || 'Failed to update availability.');
      }
    } catch (error) {
      console.error('Toggle availability error:', error);
      // toast.error(error.response?.data?.message || 'Something went wrong');
    }
  };

  // Card View Component for Mobile
  const FoodCard = ({ food, handleToggleAvailability }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="relative">
        <img
          src={food.image}
          alt={food.name}
          className="w-full h-32 object-cover cursor-pointer"
          onClick={() => openImageModal(food.image)}
        />
        <button
          onClick={() => openImageModal(food.image)}
          className="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-1.5 rounded-full opacity-0 hover:opacity-100 transition-opacity"
        >
          <Eye className="w-3 h-3" />
        </button>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h2 className="font-semibold text-gray-900 text-sm">{food.name}</h2>
          <span className="text-lg font-bold text-gray-900">₹{food.price.toFixed(2)}</span>
        </div>

        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
            {food.category}
          </span>

          <div className="flex items-center gap-1">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${food.isAvailable === true
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
              }`}>
              {food.isAvailable === true ? 'Available' : 'Unavailable'}
            </span>

            <button
              onClick={() => handleToggleAvailability(food._id)}
              className={`relative inline-flex items-center h-4 w-8 rounded-full transition-colors focus:outline-none ${food.isAvailable === true ? 'bg-green-500' : 'bg-gray-300'
                }`}
            >
              <span
                className={`inline-block w-3 h-3 transform bg-white rounded-full transition-transform ${food.isAvailable === true ? 'translate-x-4' : 'translate-x-1'
                  }`}
              />
            </button>
          </div>
        </div>

        <p className="text-sm text-gray-500 mb-3 line-clamp-2">{food.description}</p>
        <div className="flex space-x-2">
          <button
            onClick={() => handleEdit(food)}
            className="flex-1 flex items-center justify-center px-3 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors text-sm"
          >
            <Edit className="w-4 h-4 mr-1" />
            Edit
          </button>
          <button
            onClick={() => handleDelete(food._id)}
            className="flex-1 flex items-center justify-center px-3 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors text-sm"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  const fetchRestaurantId = async () => {
    try {
      const token = getAuthToken();
      const userData = localStorage.getItem('user');
      if (!userData || !token) {
        console.error('Missing user or token');
        return null;
      }

      const user = JSON.parse(userData);
      // Call the new API to get the restaurant by owner
      const response = await fetch(`${BASE_URL}/categories/by-owner/${user.id}`, {
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch restaurant: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        return result.data._id; // Return the actual restaurant ID
      } else {
        console.error('Invalid restaurant data', result);
        return null;
      }
    } catch (error) {
      console.error('Error fetching restaurant:', error);
      return null;
    }
  };

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const token = getAuthToken();

      const restaurantId = await fetchRestaurantId();

      if (!token || !restaurantId) {
        console.error('Missing token or restaurant ID');
        return;
      }

      const response = await fetch(`${BASE_URL}/categories/restaurant/${restaurantId}`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data && result.data.categories) {
        setCategories(result.data.categories);
      } else {
        console.error('Invalid response format:', result);
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };
  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line
  }, []);

  // Pagination handlers
  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate page numbers array
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  };


  if (loading) {
    return (
      <div className="p-2 lg:p-4">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading foods...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 lg:p-4">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="p-4 lg:p-6 border-b border-gray-200">
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Food Management</h1>
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm lg:text-base"
              >
                <Plus className="w-4 lg:w-5 h-4 lg:h-5 mr-2" />
                Add Food
              </button>
            </div>

            {/* Search and View Toggle */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 lg:w-5 h-4 lg:h-5" />
                <input
                  type="text"
                  placeholder="Search foods by name or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 lg:pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base"
                />
              </div>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'table'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  Table
                </button>
                <button
                  onClick={() => setViewMode('cards')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'cards'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  Cards
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {viewMode === 'cards' ? (
          // Card View (Better for Mobile)
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredFoods.map((food) => (
                <FoodCard key={food._id} food={food} handleToggleAvailability={handleToggleAvailability} />
              ))}
            </div>
            {filteredFoods.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No foods found matching your search.</p>
              </div>
            )}
          </div>
        ) : (
          // Table View (Hidden on mobile, shown on larger screens)
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Toggle Availability</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentFoods.map((food) => (
                  <tr key={food._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-16 h-16 relative">
                        <img
                          src={food.image}
                          alt={food.name}
                          className="w-full h-full rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => openImageModal(food.image)}
                        />
                        <button
                          onClick={() => openImageModal(food.image)}
                          className="absolute top-1 right-1 bg-black bg-opacity-50 text-white p-1 rounded opacity-0 hover:opacity-100 transition-opacity"
                        >
                          <Eye className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{food.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {food.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{food.price.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {food.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${food.isAvailable === true
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                        }`}>
                        {food.isAvailable === true ? 'Available' : 'Unavailable'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleToggleAvailability(food._id)}
                        className={`relative inline-flex items-center h-5 rounded-full w-10 transition-colors focus:outline-none ${food.isAvailable === true ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                      >
                        <span
                          className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${food.isAvailable === true ? 'translate-x-5' : 'translate-x-1'
                            }`}
                        />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(food)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(food._id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredFoods.length === 0 && !loading && (
              <div className="text-center py-12">
                <p className="text-gray-500">No foods found matching your search.</p>
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading foods...</p>
          </div>
        )}

        {/* Pagination */}
        {filteredFoods.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
            <div className="flex justify-between sm:hidden">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${currentPage === 1
                  ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
              >
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${currentPage === totalPages
                  ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">{startIndex + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium">
                    {Math.min(endIndex, filteredFoods.length)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium">{filteredFoods.length}</span>
                  {' '}results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium ${currentPage === 1
                      ? 'text-gray-300 bg-white border-gray-300 cursor-not-allowed'
                      : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  {getPageNumbers().map((pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageClick(pageNumber)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNumber
                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                    >
                      {pageNumber}
                    </button>
                  ))}

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium ${currentPage === totalPages
                      ? 'text-gray-300 bg-white border-gray-300 cursor-not-allowed'
                      : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Table Alternative - Show cards when table view is selected on mobile */}
        <div className="md:hidden">
          {viewMode === 'table' && (
            <div className="p-4 space-y-4">
              {filteredFoods.map((food) => (
                <div key={food._id} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <img
                      src={food.image}
                      alt={food.name}
                      className="w-16 h-16 rounded-lg object-cover cursor-pointer"
                      onClick={() => openImageModal(food.image)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <h2 className="text-sm font-medium text-gray-900">{food.name}</h2>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {food.category}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${food.isAvailable === true
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                                }`}>
                                {food.isAvailable === true ? 'Available' : 'Unavailable'}
                              </span>
                              <button
                                onClick={() => handleToggleAvailability(food._id)}
                                className={`relative inline-flex items-center h-4 w-8 rounded-full transition-colors focus:outline-none ${food.isAvailable === true ? 'bg-green-500' : 'bg-gray-300'
                                  }`}
                              >
                                <span
                                  className={`inline-block w-3 h-3 transform bg-white rounded-full transition-transform ${food.isAvailable === true ? 'translate-x-4' : 'translate-x-1'
                                    }`}
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                        <span className="text-lg font-bold text-gray-900">₹{food.price.toFixed(2)}</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-2 line-clamp-2">{food.description}</p>
                      <div className="flex space-x-2 mt-3">
                        <button
                          onClick={() => handleEdit(food)}
                          className="flex items-center px-3 py-1.5 text-blue-600 bg-blue-50 rounded text-sm"
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(food._id)}
                          className="flex items-center px-3 py-1.5 text-red-600 bg-red-50 rounded text-sm"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {filteredFoods.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">No foods found matching your search.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-screen overflow-y-auto">
            <div className="p-4 lg:p-6">
              <div className="flex items-center justify-between mb-4 lg:mb-6">
                <h2 className="text-xl lg:text-2xl font-bold text-gray-900">
                  {editingFood ? 'Edit Food' : 'Add New Food'}
                </h2>
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 lg:w-6 h-5 lg:h-6" />
                </button>
              </div>

              <div className="space-y-4 lg:space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Food Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                      disabled={categoriesLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="">
                        {categoriesLoading ? 'Loading categories...' : 'Select Category'}
                      </option>
                      {categories.map(category => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    {categories.length === 0 && !categoriesLoading && (
                      <p className="text-sm text-gray-500 mt-1">
                        No categories available. Please add categories for your restaurant first.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price (₹) *
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Food Image
                  </label>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                    <div className="flex-1 w-full sm:w-auto">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="image-upload"
                      />
                      <label
                        htmlFor="image-upload"
                        className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Image
                      </label>
                    </div>
                    {imagePreview && (
                      <div className="w-20 h-20">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-full h-full rounded-lg object-cover border"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingFood ? 'Update Food' : 'Add Food'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image View Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={selectedImage}
              alt="Food"
              className="max-w-full max-h-full rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Orders List Component
const OrdersList = ({ orders, setOrders }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const socketRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      if (mounted) {
        await fetchOrders();
        initializeSocket();
      }
    };

    initialize();

    return () => {
      mounted = false;
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, []);

  const initializeSocket = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found');
      return;
    }

    // Don't create multiple connections
    if (socketRef.current?.connected) {
      return;
    }

    // Clean up existing connection
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
    }

    // Initialize socket connection
    socketRef.current = io('https://takeawaybackend.onrender.com', {
      auth: {
        token: token
      },
      transports: ['websocket', 'polling'],
      forceNew: true,
      timeout: 10000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000
    });

    // Connection event handlers
    socketRef.current.on('connect', () => {
      setConnectionStatus('connected');
      setError(null);

      // Authenticate with the server and join appropriate rooms
      const userRole = getUserRole();
      const userId = getUserId();
      const restaurantId = getRestaurantId();

      // Send authentication data to server
      socketRef.current.emit('authenticate', {
        userId: userId,
        userRole: userRole,
        restaurantId: restaurantId
      });

      console.log(`Connected as ${userRole} with ID: ${userId}`);
    });

    socketRef.current.on('disconnect', (reason) => {
      setConnectionStatus('disconnected');
      console.log('Disconnected:', reason);
    });

    socketRef.current.on('connect_error', (error) => {
      setConnectionStatus('error');
      setError(`Failed to connect: ${error.message}`);
      console.error('Connection error:', error);
    });

    socketRef.current.on('reconnect', (attemptNumber) => {
      setConnectionStatus('connected');
      setError(null);
      console.log('Reconnected after', attemptNumber, 'attempts');
    });

    socketRef.current.on('reconnect_error', (error) => {
      setConnectionStatus('error');
      console.error('Reconnection error:', error);
    });

    // MongoDB Change Stream event handlers
    // These events are automatically emitted by the backend change streams

    // Listen for new orders (from change stream)
    socketRef.current.on('newOrder', (orderData) => {
      console.log('New order received via change stream:', orderData);
      addNewOrderToList(orderData);
    });

    // Listen for order status updates (from change stream)
    socketRef.current.on('orderStatusUpdate', (updateData) => {
      console.log('Order status update received via change stream:', updateData);
      updateOrderInList(updateData.orderId, {
        status: updateData.status,
        estimatedTime: updateData.estimatedTime
      });
    });

    // Listen for order cancellations (from change stream) - Note: This may not be needed with new/delivered model
    socketRef.current.on('orderCancelled', (cancelData) => {
      console.log('Order cancelled via change stream:', cancelData);
      updateOrderInList(cancelData.orderId, {
        status: 'delivered' // Since cancelled is not available, we might use delivered
      });

      // Show notification for cancelled orders
      if (cancelData.reason) {
        console.log(`Order ${cancelData.orderNumber} cancelled: ${cancelData.reason}`);
      }
    });

    // Listen for broadcast events (fallback)
    socketRef.current.on('newOrderBroadcast', (orderData) => {
      const userRole = getUserRole();
      const userId = getUserId();
      const restaurantId = getRestaurantId();

      // Check if this broadcast is relevant to the current user
      if (userRole === 'admin' ||
        (userRole === 'restaurant' && orderData.targetRestaurant === restaurantId) ||
        (userRole === 'restaurant' && orderData.targetOwner === userId)) {
        console.log('New order broadcast received:', orderData);
        addNewOrderToList(orderData);
      }
    });

    // Handle general errors
    socketRef.current.on('error', (error) => {
      console.error('Socket error:', error);
      setError(error.message);
    });

    // Handle pong responses for health checks
    socketRef.current.on('pong', () => {
      console.log('Pong received - connection healthy');
    });
  };

  // Helper function to get user ID
  const getUserId = () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.userId || payload.id;
      }
    } catch (error) {
      console.error('Error getting user ID:', error);
    }
    return null;
  };

  // Helper function to get user role
  const getUserRole = () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.role;
      }
    } catch (error) {
      console.error('Error getting user role:', error);
    }
    return null;
  };

  // Helper function to get restaurant ID
  const getRestaurantId = () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.restaurantId;
      }
    } catch (error) {
      console.error('Error getting restaurant ID:', error);
    }
    return null;
  };

  const updateOrderInList = (orderId, updates) => {
    setOrders(prev => prev.map(order =>
      order._id === orderId || order.id === orderId
        ? { ...order, ...updates }
        : order
    ));

    // Update selected order if it's being viewed
    if (selectedOrder && (selectedOrder._id === orderId || selectedOrder.id === orderId)) {
      setSelectedOrder(prev => ({ ...prev, ...updates }));
    }
  };

  const addNewOrderToList = (newOrder) => {
    // Transform the new order to match your component structure
    const transformedOrder = {
      id: newOrder.orderNumber,
      _id: newOrder._id,
      customerName: newOrder.customerName,
      customerPhone: newOrder.customerPhone,
      items: newOrder.items.map(item => ({
        name: item.name,
        price: item.price,
        quantity: item.quantity
      })),
      status: newOrder.status,
      timestamp: newOrder.createdAt || newOrder.timestamp,
      totalAmount: newOrder.totalAmount
    };

    setOrders(prev => {
      // Check if order already exists to prevent duplicates
      const existingOrder = prev.find(order =>
        order._id === transformedOrder._id || order.id === transformedOrder.id
      );

      if (existingOrder) {
        return prev; // Order already exists, don't add duplicate
      }

      return [transformedOrder, ...prev];
    });
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${BASE_URL}/orders/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const result = await response.json();

      if (result.success) {
        const transformedOrders = result.data.map(order => ({
          id: order.orderNumber,
          _id: order._id,
          customerName: order.customerName,
          customerPhone: order.customerPhone,
          items: order.items.map(item => ({
            name: item.name,
            price: item.price,
            quantity: item.quantity
          })),
          status: order.status,
          timestamp: order.createdAt,
          totalAmount: order.totalAmount
        }));

        setOrders(transformedOrders);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      (order.customerName && order.customerName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      order.id.toString().includes(searchTerm) ||
      (order.items && order.items.some(item =>
        item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())
      ));

    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const sortedOrders = filteredOrders.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      updateOrderInList(orderId, { status: newStatus });
      const token = localStorage.getItem('token');
      const orderToUpdate = orders.find(order => order.id === orderId || order._id === orderId);
      if (!orderToUpdate) return;

      // Make HTTP request to update status
      // The MongoDB change stream will automatically handle the real-time updates
      const response = await fetch(`${BASE_URL}/orders/${orderToUpdate._id || orderId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update order status');
      }

      // No need to manually update the UI here - the change stream will handle it
      console.log('Order status update request sent - waiting for change stream notification');

    } catch (err) {
      setError('Failed to update order status');
      console.error('Error updating order status:', err);
    }
  };

  // Send periodic heartbeat to check connection
  useEffect(() => {
    const heartbeat = setInterval(() => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('ping');
      }
    }, 30000); // Every 30 seconds

    return () => clearInterval(heartbeat);
  }, []);

  // Updated status color function for new/delivered only
  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const calculateTotal = (items) => {
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  //pagination
  const totalPages = Math.ceil(sortedOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOrders = sortedOrders.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Pagination handlers
  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate page numbers array
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center">
        <div className="text-gray-500">Loading orders...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-700">Error: {error}</div>
          <button
            onClick={() => {
              setError(null);
              fetchOrders();
              initializeSocket();
            }}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Orders Management</h1>
              <div className="flex items-center gap-4">
                {/* <div className="text-xs sm:text-sm text-gray-500">
                  Orders are displayed in chronological order
                </div> */}
              </div>
            </div>

            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                <input
                  type="text"
                  placeholder="Search by customer, ID, or item..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="delivered">Delivered</option>
              </select>
            </div>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">#{order.id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8">
                        <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-600" />
                        </div>
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">{order.customerName}</div>
                        <div className="text-sm text-gray-500">{order.customerPhone}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {order.items.slice(0, 2).map((item, index) => (
                        <div key={index}>
                          {item.name} x{item.quantity}
                        </div>
                      ))}
                      {order.items.length > 2 && (
                        <div className="text-xs text-gray-400">
                          +{order.items.length - 2} more items
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ₹{calculateTotal(order.items).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {formatTime(order.timestamp)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openOrderDetails(order)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <select
                        value={order.status}
                        onChange={(e) => updateOrderStatus(order._id || order.id, e.target.value)}
                        className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="new">New</option>
                        <option value="delivered">Delivered</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden">
          {sortedOrders.map((order) => (
            <div key={order.id} className="border-b border-gray-200 p-4 hover:bg-gray-50">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center mr-3">
                    <User className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">#{order.id}</div>
                    <div className="text-sm text-gray-500">{order.customerName}</div>
                  </div>
                </div>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                  {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                </span>
              </div>

              <div className="space-y-2 mb-3">
                <div className="text-sm text-gray-600">
                  <strong>Items:</strong> {order.items.slice(0, 2).map((item, index) => (
                    <span key={index}>
                      {index > 0 && ', '}
                      {item.name} x{item.quantity}
                    </span>
                  ))}
                  {order.items.length > 2 && (
                    <span className="text-xs text-gray-400"> +{order.items.length - 2} more</span>
                  )}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center text-gray-500">
                    <Clock className="w-4 h-4 mr-1" />
                    {new Date(order.timestamp).toLocaleTimeString()}
                  </div>
                  <div className="font-semibold text-gray-900">
                    ₹{calculateTotal(order.items).toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <button
                  onClick={() => openOrderDetails(order)}
                  className="text-blue-600 hover:text-blue-900 text-sm font-medium flex items-center"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View Details
                </button>
                <select
                  value={order.status}
                  onChange={(e) => updateOrderStatus(order._id || order.id, e.target.value)}
                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="new">New</option>
                  <option value="delivered">Delivered</option>
                </select>
              </div>
            </div>
          ))}
        </div>

        {sortedOrders.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-500">No orders found matching your criteria.</p>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-screen overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Order #{selectedOrder.id}</h2>
                <button
                  onClick={() => setShowOrderModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              <div className="space-y-4 sm:space-y-6">
                {/* Customer Info */}
                <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Customer Information</h2>
                  <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
                    <div className="flex items-center">
                      <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mr-2 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{selectedOrder.customerName}</div>
                        <div className="text-xs text-gray-500">Customer Name</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mr-2 flex items-center justify-center text-sm">📞</div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{selectedOrder.customerPhone}</div>
                        <div className="text-xs text-gray-500">Phone Number</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Order Items</h2>

                  {/* Mobile Items List */}
                  <div className="sm:hidden space-y-3">
                    {selectedOrder.items.map((item, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-medium text-gray-900 flex-1">{item.name}</div>
                          <div className="text-sm font-semibold text-gray-900 ml-2">
                            ₹{(item.price * item.quantity).toFixed(2)}
                          </div>
                        </div>
                        <div className="flex justify-between text-sm text-gray-500">
                          <span>Qty: {item.quantity}</span>
                          <span>₹{item.price.toFixed(2)} each</span>
                        </div>
                      </div>
                    ))}
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-gray-900">Total:</span>
                        <span className="font-bold text-lg text-gray-900">
                          ₹{calculateTotal(selectedOrder.items).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Desktop Items Table */}
                  <div className="hidden sm:block border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedOrder.items.map((item, index) => (
                          <tr key={index}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.name}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{item.quantity}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">₹{item.price.toFixed(2)}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">₹{(item.price * item.quantity).toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr className="bg-gray-50">
                          <td colSpan="3" className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">Total:</td>
                          <td className="px-4 py-3 text-sm font-bold text-gray-900">₹{calculateTotal(selectedOrder.items).toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Order Status */}
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Order Status</h2>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-gray-50 rounded-lg gap-3">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">Order Time</div>
                        <div className="text-xs text-gray-500">{formatTime(selectedOrder.timestamp)}</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedOrder.status)}`}>
                        {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Status Update */}
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Update Status</h2>
                  <select
                    value={selectedOrder.status}
                    onChange={(e) => updateOrderStatus(selectedOrder._id || selectedOrder.id, e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="new">New</option>
                    <option value="delivered">Delivered</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Pagination */}
      {sortedOrders.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
          <div className="flex justify-between sm:hidden">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${currentPage === 1
                  ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
            >
              Previous
            </button>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${currentPage === totalPages
                  ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">{startIndex + 1}</span>
                {' '}to{' '}
                <span className="font-medium">
                  {Math.min(endIndex, sortedOrders.length)}
                </span>
                {' '}of{' '}
                <span className="font-medium">{sortedOrders.length}</span>
                {' '}results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium ${currentPage === 1
                      ? 'text-gray-300 bg-white border-gray-300 cursor-not-allowed'
                      : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                {getPageNumbers().map((pageNumber) => (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageClick(pageNumber)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNumber
                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                  >
                    {pageNumber}
                  </button>
                ))}

                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium ${currentPage === totalPages
                      ? 'text-gray-300 bg-white border-gray-300 cursor-not-allowed'
                      : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RestaurantSettings = () => {
  const [restaurantData, setRestaurantData] = useState({
    name: '',
    owner: '',
    // cuisine: '',
    description: '',
    address: '',
    phone: '',
    image: ''
  });

  const [originalData, setOriginalData] = useState({});
  const [isOpen, setIsOpen] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Get auth token from localStorage or context
  const getAuthToken = () => {
    return localStorage.getItem('token'); // Adjust based on your auth implementation
  };

  // Fetch restaurant profile on component mount
  useEffect(() => {
    fetchRestaurantProfile();
    // eslint-disable-next-line
  }, []);

  const fetchRestaurantProfile = async () => {
    try {
      setLoading(true);
      setError('');

      const token = getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please login again.');
        return;
      }

      const response = await fetch(`${BASE_URL}/restaurant/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success && data.restaurant) {
        const restaurant = data.restaurant;
        const profileData = {
          name: restaurant.name || '',
          owner: restaurant.owner?.name || '',

          description: restaurant.description || '',
          address: restaurant.address || '',
          phone: restaurant.phone || '',
          image: restaurant.image
        };

        setRestaurantData(profileData);
        setOriginalData(profileData);
        setIsOpen(restaurant.isOpen !== false); // Default to open if not specified
      } else {
        setError(data.message || 'Failed to fetch restaurant profile');
      }
    } catch (err) {
      console.error('Error fetching restaurant profile:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRestaurantData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleToggleStatus = async () => {
    try {
      setToggleLoading(true);
      setError('');

      const token = getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please login again.');
        return;
      }

      const response = await fetch(`${BASE_URL}/restaurant/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setIsOpen(data.isOpen);
        setShowSuccessMessage(true);

        // Hide success message after 3 seconds
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 3000);
      } else {
        setError(data.message || 'Failed to update restaurant status');
      }
    } catch (err) {
      console.error('Error toggling restaurant status:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setToggleLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }

      setImageFile(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setError(''); // Clear any previous errors
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const token = getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please login again.');
        return;
      }

      // Create FormData for multipart/form-data request
      const formData = new FormData();

      // Add text fields
      formData.append('name', restaurantData.name);
      formData.append('description', restaurantData.description);
      formData.append('address', restaurantData.address);
      formData.append('phone', restaurantData.phone);
      formData.append('cuisine', restaurantData.cuisine);

      // Add image file if selected
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const response = await fetch(`${BASE_URL}/restaurant/update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type header when using FormData
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        // Update the current data with the response
        if (data.restaurant) {
          const updatedData = {
            name: data.restaurant.name || '',
            owner: data.restaurant.owner?.name || '',
            // cuisine: data.restaurant.cuisine || '',
            description: data.restaurant.description || '',
            address: data.restaurant.address || '',
            phone: data.restaurant.phone || '',
            image: data.restaurant.image || restaurantData.image
          };

          setRestaurantData(updatedData);
          setOriginalData(updatedData);
        }

        setIsEditing(false);
        setImagePreview('');
        setImageFile(null);
        setShowSuccessMessage(true);

        // Show warning if image upload had issues
        if (data.warning) {
          console.warn(data.warning);
        }

        // Hide success message after 3 seconds
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 3000);
      } else {
        if (data.errors && Array.isArray(data.errors)) {
          // Handle validation errors
          const errorMessages = data.errors.map(err => err.message).join(', ');
          setError(errorMessages);
        } else {
          setError(data.message || 'Failed to update restaurant information');
        }
      }
    } catch (err) {
      console.error('Error updating restaurant:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to original data
    setRestaurantData(originalData);
    setIsEditing(false);
    setImagePreview('');
    setImageFile(null);
    setError('');
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-2 lg:p-4">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading restaurant information...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 lg:p-4">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="p-4 lg:p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 flex items-center">
                <Settings className="w-6 lg:w-8 h-6 lg:h-8 mr-2 lg:mr-3" />
                Restaurant Settings
              </h1>
              <p className="text-sm lg:text-base text-gray-600 mt-1">
                Manage your restaurant information and branding
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              {/* Open/Close Toggle */}
              <div className="flex items-center space-x-3">
                <span className="text-sm lg:text-base font-medium text-gray-700">
                  Restaurant Status:
                </span>
                <button
                  onClick={handleToggleStatus}
                  disabled={toggleLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${isOpen
                    ? 'bg-green-600 focus:ring-green-500'
                    : 'bg-red-600 focus:ring-red-500'
                    }`}
                >
                  {toggleLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : (
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isOpen ? 'translate-x-6' : 'translate-x-1'
                        }`}
                    />
                  )}
                </button>
                <span className={`text-sm lg:text-base font-semibold ${isOpen ? 'text-green-600' : 'text-red-600'
                  }`}>
                  {isOpen ? 'OPEN' : 'CLOSED'}
                </span>
              </div>

              {/* Edit Buttons */}
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm lg:text-base"
                >
                  Edit Information
                </button>
              ) : (
                <div className="flex space-x-2">
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="inline-flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm lg:text-base disabled:opacity-50"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm lg:text-base disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Success Message */}
        {showSuccessMessage && (
          <div className="p-4 mx-4 lg:mx-6 mt-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Restaurant information updated successfully!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-4 mx-4 lg:mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">
                  {error}
                </p>
              </div>
              <button
                onClick={() => setError('')}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-4 lg:p-6">
          <div className="space-y-6 lg:space-y-8">
            {/* Restaurant Image */}
            <div className="text-center">
              <div className="relative inline-block">
                <img
                  src={imagePreview || restaurantData.image}
                  alt="Restaurant"
                  className="w-48 h-32 lg:w-64 lg:h-40 rounded-lg object-cover border-2 border-gray-200 shadow-sm"
                />
                {isEditing && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <label htmlFor="restaurant-image" className="cursor-pointer">
                      <Upload className="w-8 h-8 text-white" />
                      <input
                        type="file"
                        id="restaurant-image"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                )}
              </div>
              {isEditing && (
                <p className="text-sm text-gray-500 mt-2">
                  Click on the image to upload a new restaurant photo (Max 5MB)
                </p>
              )}
              {imageFile && (
                <p className="text-sm text-blue-600 mt-1">
                  New image selected: {imageFile.name}
                </p>
              )}
            </div>

            {/* Restaurant Information Form */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Restaurant Name */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Restaurant Name *
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="name"
                    value={restaurantData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-lg font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter restaurant name"
                    required
                  />
                ) : (
                  <div className="px-4 py-3 bg-gray-50 rounded-lg">
                    <h2 className="text-lg lg:text-xl font-semibold text-gray-900">
                      {restaurantData.name || 'Not provided'}
                    </h2>
                  </div>
                )}
              </div>

              {/* Owner Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Owner Name
                </label>
                <div className="px-3 py-2 bg-gray-50 rounded-lg">
                  <p className="text-sm lg:text-base text-gray-900">
                    {restaurantData.owner || 'Not provided'}
                  </p>
                </div>
              </div>

              {/* Cuisine */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cuisine *
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="cuisine"
                    value={restaurantData.cuisine}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base"
                    placeholder="Enter cuisine type"
                  />
                ) : (
                  <div className="px-3 py-2 bg-gray-50 rounded-lg">
                    <p className="text-sm lg:text-base text-gray-900">
                      {restaurantData.cuisine || 'Not provided'}
                    </p>
                  </div>
                )}
              </div> */}

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    name="phone"
                    value={restaurantData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base"
                    placeholder="Enter phone number"
                  />
                ) : (
                  <div className="px-3 py-2 bg-gray-50 rounded-lg">
                    <p className="text-sm lg:text-base text-gray-900">
                      {restaurantData.phone || 'Not provided'}
                    </p>
                  </div>
                )}
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address *
                </label>
                {isEditing ? (
                  <textarea
                    name="address"
                    value={restaurantData.address}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base resize-none"
                    placeholder="Enter complete address"
                  />
                ) : (
                  <div className="px-3 py-2 bg-gray-50 rounded-lg">
                    <p className="text-sm lg:text-base text-gray-900">
                      {restaurantData.address || 'Not provided'}
                    </p>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                {isEditing ? (
                  <textarea
                    name="description"
                    value={restaurantData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base resize-none"
                    placeholder="Enter restaurant description"
                  />
                ) : (
                  <div className="px-3 py-2 bg-gray-50 rounded-lg">
                    <p className="text-sm lg:text-base text-gray-700 leading-relaxed">
                      {restaurantData.description || 'No description provided'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

//New Order History 

const TransactionDetails = ({ orders, setOrders, restaurantId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const getAuthToken = () => {
    return localStorage.getItem('token'); // Adjust based on your auth implementation
  };
  useEffect(() => {
    const fetchTransactions = async () => {
      // Don't fetch if no restaurantId provided
      // if (!restaurantId) {
      //   setError('Restaurant ID is required');
      //   return;
      // }

      setLoading(true);
      setError(null);

      try {
        const token = getAuthToken();
        const userData = localStorage.getItem('user');
        if (!userData || !token) {
          console.error('Missing user or token');
          return null;
        }

        const user = JSON.parse(userData);

        // Updated API endpoint - note the /:id parameter might need to be replaced with actual ID
        // If you need all transactions, the endpoint might be different
        const response = await fetch(`${BASE_URL}/orders/transactions/${user.id}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const result = await response.json();

        if (response.ok && result.success) {
          // Set transactions data
          setTransactions(result.data);
          setMonthlyRevenue(result.monthlyRevenue || 0);

          // Map API response to match your component's expected structure
          const formattedOrders = result.data.map((transaction) => ({
            id: transaction.orderId,
            transactionId: transaction.transactionId,
            timestamp: transaction.orderTime,
            items: [
              {
                name: "Total Items",
                quantity: transaction.itemCount,
                price: transaction.total / transaction.itemCount,
              },
            ],
            status: 'delivered', // Assuming all transactions are completed
            total: transaction.total
          }));

          setOrders(formattedOrders);
        } else {
          throw new Error(result.message || "Failed to fetch transactions");
        }
      } catch (err) {
        console.error("Error fetching transaction details:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [setOrders, currentPage]); // Added currentPage as dependency but API doesn't use it

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter]);

  // Helper functions
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const calculateTotal = (items) => {
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  // Use the monthlyRevenue from API instead of calculating
  const getTotalRevenue = () => {
    return monthlyRevenue;
  };

  // Since these are transaction details, all should be completed
  const completedOrders = orders;

  const filteredOrders = completedOrders.filter((order) => {
    const matchesSearch =
      order.id.toString().includes(searchTerm) ||
      (order.transactionId && order.transactionId.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.items && order.items.some(item =>
        item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())
      ));

    let matchesDate = true;
    if (dateFilter !== 'all') {
      const orderDate = new Date(order.timestamp);
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      switch (dateFilter) {
        case 'today':
          matchesDate = orderDate >= startOfToday;
          break;
        case 'yesterday':
          const yesterday = new Date(startOfToday);
          yesterday.setDate(yesterday.getDate() - 1);
          matchesDate = orderDate >= yesterday && orderDate < startOfToday;
          break;
        case 'week':
          const weekAgo = new Date(startOfToday);
          weekAgo.setDate(weekAgo.getDate() - 7);
          matchesDate = orderDate >= weekAgo;
          break;
        case 'month':
          const monthAgo = new Date(startOfToday);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          matchesDate = orderDate >= monthAgo;
          break;
        default:
          matchesDate = true;
      }
    }

    return matchesSearch && matchesDate;
  });

  const sortedOrders = filteredOrders.sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
  );

  // Pagination logic
  const totalPages = Math.ceil(sortedOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOrders = sortedOrders.slice(startIndex, endIndex);

  // Calculate total for current page and overall total
  const currentPageTotal = currentOrders.reduce((sum, order) =>
    sum + (order.total || calculateTotal(order.items)), 0
  );
  const overallTotal = sortedOrders.reduce((sum, order) =>
    sum + (order.total || calculateTotal(order.items)), 0
  );

  if (loading) {
    return (
      <div className="p-2 sm:p-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Loading transactions...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-2 sm:p-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-red-500">Error: {error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Transaction Details</h1>
              <div className="flex items-center gap-4">
                <div className="text-xs sm:text-sm font-bold text-green-500">
                  Monthly Revenue: ₹{getTotalRevenue().toFixed(2)}
                </div>
              </div>
            </div>

            {/* Search and Date Filter */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                <input
                  type="text"
                  placeholder="Search by order ID, transaction ID, or item..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </select>
            </div>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item Count</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Time</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">#{order.id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{order.transactionId}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {order.items.slice(0, 2).map((item, index) => (
                        <div key={index}>
                          {item.name} x{item.quantity}
                        </div>
                      ))}
                      {order.items.length > 2 && (
                        <div className="text-xs text-gray-400">
                          +{order.items.length - 2} more items
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ₹{order.total ? order.total.toFixed(2) : calculateTotal(order.items).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {formatTime(order.timestamp)}
                    </div>
                  </td>
                </tr>
              ))}
              {/* Total Row */}
              <tr className="bg-gray-50 font-semibold">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  Total ({sortedOrders.length} transactions)
                </td>
                <td className="px-6 py-4 whitespace-nowrap"></td>
                <td className="px-6 py-4 whitespace-nowrap"></td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ₹{overallTotal.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap"></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden">
          {currentOrders.map((order) => (
            <div key={order.id} className="border-b border-gray-200 p-4 hover:bg-gray-50">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <div>
                    <div className="font-medium text-gray-900">#{order.id}</div>
                    <div className="text-sm text-gray-500">{order.transactionId}</div>
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-3">
                <div className="text-sm text-gray-600">
                  <strong>Items:</strong> {order.items.slice(0, 2).map((item, index) => (
                    <span key={index}>
                      {index > 0 && ', '}
                      {item.name} x{item.quantity}
                    </span>
                  ))}
                  {order.items.length > 2 && (
                    <span className="text-xs text-gray-400"> +{order.items.length - 2} more</span>
                  )}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center text-gray-500">
                    <Clock className="w-4 h-4 mr-1" />
                    {new Date(order.timestamp).toLocaleTimeString()}
                  </div>
                  <div className="font-semibold text-gray-900">
                    ₹{order.total ? order.total.toFixed(2) : calculateTotal(order.items).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Mobile Total */}
          <div className="border-b border-gray-200 p-4 bg-gray-50">
            <div className="flex justify-between items-center font-semibold">
              <span>Total ({sortedOrders.length} transactions)</span>
              <span>₹{overallTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">{startIndex + 1}</span>
                    {' '}to{' '}
                    <span className="font-medium">
                      {Math.min(endIndex, sortedOrders.length)}
                    </span>
                    {' '}of{' '}
                    <span className="font-medium">{sortedOrders.length}</span>
                    {' '}results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>

                    {/* Page Numbers */}
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      const isCurrentPage = pageNumber === currentPage;

                      // Show first page, last page, current page, and pages around current page
                      const showPage = pageNumber === 1 ||
                        pageNumber === totalPages ||
                        Math.abs(pageNumber - currentPage) <= 1;

                      if (!showPage) {
                        // Show ellipsis for gaps
                        if ((pageNumber === 2 && currentPage > 4) ||
                          (pageNumber === totalPages - 1 && currentPage < totalPages - 3)) {
                          return (
                            <span key={pageNumber} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                              ...
                            </span>
                          );
                        }
                        return null;
                      }

                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${isCurrentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentOrders.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-500">No transactions found matching your criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};


const RestaurantManagement = () => {
  const [activeSection, setActiveSection] = useState('foods');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [orders, setOrders] = useState([]);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900">Restaurant Admin</h1>
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-600 hover:text-gray-900"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      <div className="flex">
        <Sidebar
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          isOpen={sidebarOpen}
          setIsOpen={setSidebarOpen}
        />

        <div className="flex-1 lg:ml-0">
          {activeSection === 'foods' && <FoodManagement />}
          {activeSection === 'orders' && <OrdersList orders={orders} setOrders={setOrders} />}
          {activeSection === 'Transaction' && <TransactionDetails orders={orders} setOrders={setOrders} />}
          {activeSection === 'settings' && <RestaurantSettings />}
        </div>
      </div>
    </div>
  );
}

export default RestaurantManagement
